import type { Worksheet } from '@tableau/extensions-api-types';
import type { ExtensionSettings, FieldMapping } from '../config/types';
import { getTableau } from './init';

const DEFAULT_DROPDOWN_OPTIONS = ['Data Quality Issue','Unable to Act','Already Addressed','Timing Not Optimal'];
const DEFAULT_WORKSHEET_NAME = 'Alerts List';
const DEFAULT_FIELD_MAPPING: FieldMapping = {
  keyField: 'alert_id',
};

export function getExtensionSettings(): ExtensionSettings {
  const tableauGlobal =
    typeof window !== 'undefined'
      ? (window as typeof window & { tableau?: unknown }).tableau
      : undefined;

  if (!tableauGlobal) {
    return {
      worksheetName: DEFAULT_WORKSHEET_NAME,
      fieldMapping: DEFAULT_FIELD_MAPPING,
      dropdownOptions: DEFAULT_DROPDOWN_OPTIONS,
      dashboardLabel: 'Mock Dashboard',
    };
  }

  const tableau = getTableau();
  const settings = tableau.extensions.settings;

  const worksheetName = settings.get('worksheetName')?.trim() || DEFAULT_WORKSHEET_NAME;
  const fieldMappingRaw = settings.get('fieldMapping');
  const dropdownOptionsRaw = settings.get('dropdownOptions');
  const dashboardLabel = settings.get('dashboardLabel') ?? undefined;

  let fieldMapping: FieldMapping = { ...DEFAULT_FIELD_MAPPING };
  if (fieldMappingRaw) {
    try {
      fieldMapping = {
        ...DEFAULT_FIELD_MAPPING,
        ...(JSON.parse(fieldMappingRaw) as FieldMapping),
      };
    } catch {
      console.warn('Failed to parse fieldMapping from settings');
    }
  }

  let dropdownOptions = DEFAULT_DROPDOWN_OPTIONS;
  if (dropdownOptionsRaw) {
    try {
      const parsed = JSON.parse(dropdownOptionsRaw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        dropdownOptions = parsed as string[];
      }
    } catch {
      console.warn('Failed to parse dropdownOptions from settings');
    }
  }

  return {
    worksheetName,
    fieldMapping,
    dropdownOptions,
    dashboardLabel,
  };
}

export async function saveExtensionSettings(settings: Partial<ExtensionSettings>): Promise<void> {
  const tableau = getTableau();
  const extensionSettings = tableau.extensions.settings;

  if (settings.worksheetName !== undefined) {
    extensionSettings.set('worksheetName', settings.worksheetName);
  }
  if (settings.fieldMapping !== undefined) {
    extensionSettings.set('fieldMapping', JSON.stringify(settings.fieldMapping));
  }
  if (settings.dropdownOptions !== undefined) {
    extensionSettings.set('dropdownOptions', JSON.stringify(settings.dropdownOptions));
  }
  if (settings.dashboardLabel !== undefined) {
    extensionSettings.set('dashboardLabel', settings.dashboardLabel);
  }

  await extensionSettings.saveAsync();
}

export function getAvailableWorksheets(): string[] {
  const tableau = getTableau();
  const dashboard = tableau.extensions.dashboardContent?.dashboard;
  if (!dashboard) return [];

  return dashboard.worksheets.map((ws: Worksheet) => ws.name);
}
